/**
 * CytoscapeGraph.tsx
 *
 * Cytoscape.js-based graph visualization for FAIR ontology
 * Displays nodes and edges with interactive features
 */

import React, { useEffect, useRef } from 'react';
import cytoscape, { Core } from 'cytoscape';

type GraphNode = {
  id: string;
  label: string;
  kind: 'Class' | 'Individual' | 'Property';  // GraphNode uses 'kind' instead of 'type'
  parent?: string;
  pillar?: 'F' | 'A' | 'I' | 'R';
  meta?: Record<string, unknown>;
};

type GraphEdge = {
  id?: string;
  source: string;
  target: string;
  label: string;
  kind?: 'abox' | 'tbox';
  pillar?: 'F' | 'A' | 'I' | 'R';
  status?: 'satisfied' | 'missing' | 'unknown';
  satisfiedBy?: string[];
};

interface CytoscapeGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  onNodeClick?: (nodeId: string) => void;
  onEdgeClick?: (edge: GraphEdge) => void;
}

export function CytoscapeGraph({ nodes, edges, onNodeClick, onEdgeClick }: CytoscapeGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      console.error('[CytoscapeGraph] Container not available');
      return;
    }

    // Convert nodes and edges to Cytoscape format
    const elements = [
      ...nodes.map(n => ({
        data: {
          id: n.id,
          label: n.label,
          kind: n.kind,  // GraphNode uses 'kind' not 'type'
          parent: n.parent,
          pillar: n.pillar,
          meta: n.meta
        }
      })),
      ...edges.map((e, i) => ({
        data: {
          id: e.id || `edge_${i}`,  // Use edge ID if available
          source: e.source,
          target: e.target,
          label: e.label,
          edgeKind: e.kind,  // Rename to avoid conflict with node kind
          pillar: e.pillar,
          status: e.status,
          satisfiedBy: e.satisfiedBy
        }
      }))
    ];

    // Initialize Cytoscape
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'font-size': '12px',
            'background-color': '#94a3b8', // gray-400
            'color': '#1e293b',
            'width': 60,
            'height': 60,
            'border-width': 2,
            'border-color': '#64748b' // gray-500
          }
        },
        {
          selector: 'node[kind="Class"]',
          style: {
            'background-color': '#3b82f6', // blue-500
            'shape': 'rectangle'
          }
        },
        {
          selector: 'node[kind="Individual"]',
          style: {
            'background-color': '#a855f7', // purple-500
            'shape': 'ellipse'
          }
        },
        {
          selector: 'node[kind="Property"]',
          style: {
            'background-color': '#22c55e', // green-500
            'shape': 'diamond'
          }
        },
        {
          selector: 'node[pillar="F"]',
          style: {
            'border-color': '#3b82f6' // blue-500
          }
        },
        {
          selector: 'node[pillar="A"]',
          style: {
            'border-color': '#22c55e' // green-500
          }
        },
        {
          selector: 'node[pillar="I"]',
          style: {
            'border-color': '#f59e0b' // amber-500
          }
        },
        {
          selector: 'node[pillar="R"]',
          style: {
            'border-color': '#ec4899' // pink-500
          }
        },
        {
          selector: 'edge',
          style: {
            'label': 'data(label)',
            'font-size': '10px',
            'curve-style': 'bezier',
            'target-arrow-shape': 'triangle',
            'line-color': '#94a3b8', // gray-400
            'target-arrow-color': '#94a3b8',
            'width': 2
          }
        },
        {
          selector: 'edge[status="satisfied"]',
          style: {
            'line-color': '#22c55e', // green-500
            'target-arrow-color': '#22c55e',
            'width': 3
          }
        },
        {
          selector: 'edge[status="missing"]',
          style: {
            'line-color': '#ef4444', // red-500
            'target-arrow-color': '#ef4444',
            'line-style': 'dashed'
          }
        },
        {
          selector: 'edge[status="unknown"]',
          style: {
            'line-color': '#9ca3af', // gray-400
            'target-arrow-color': '#9ca3af',
            'line-style': 'dotted',
            'width': 1
          }
        },
        {
          selector: ':selected',
          style: {
            'border-width': 4,
            'border-color': '#f59e0b', // amber-500
            'background-color': '#fbbf24' // amber-400
          }
        }
      ],
      layout: {
        name: 'breadthfirst',
        directed: true,
        spacingFactor: 1.5,
        animate: true,
        animationDuration: 500
      },
      minZoom: 0.3,
      maxZoom: 3,
      wheelSensitivity: 0.1
    });

    cyRef.current = cy;

    // Node click handler
    if (onNodeClick) {
      cy.on('tap', 'node', (event: { target: { id: () => string } }) => {
        const node = event.target;
        onNodeClick(node.id());
      });
    }

    // Edge click handler
    if (onEdgeClick) {
      cy.on('tap', 'edge', (event: { target: { data: () => { source: string; target: string; label: string } } }) => {
        const edge = event.target;
        const edgeData = edge.data();

        // Find the original edge object
        const originalEdge = edges.find(e =>
          e.source === edgeData.source &&
          e.target === edgeData.target &&
          e.label === edgeData.label
        );

        if (originalEdge) {
          onEdgeClick(originalEdge);
        }
      });
    }

    // Cleanup
    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [nodes, edges, onNodeClick, onEdgeClick]);

  return (
    <div
      ref={containerRef}
      className="cytoscape-graph"
      style={{
        width: '100%',
        height: '600px',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#ffffff'
      }}
    />
  );
}

// Type declaration for Cytoscape global
declare global {
  interface Window {
    cytoscape: typeof cytoscape;
  }
}
